import { KoobInputSelect } from '@koob/ui';
import { useField } from 'formik';
import { FormField, FormLabel, ErrorMessageWrapper } from 'ui/forms';
import { Stack } from '@koob/margaret';
import { isArray } from 'lodash';

const formatError = (error) => {
  if (isArray(error)) {
    return error.filter(Boolean)?.[0];
  }
  return error;
};

const LocalMultiSearchableSelectField = ({
  name,
  label,
  rightLabel,
  onSelectOption,
  touchRequired = true,
  options,
  required,
  renderOption,
  disabled,
  loading,
  placeholder,
  // keep at most one selected option; picking another replaces it
  single = false,
}) => {
  const [{ value }, { touched, error }, { setValue }] = useField({ name });
  const hasError =
    (!touchRequired || Boolean(touched)) && Boolean(formatError(error));
  const selected = value ?? [];
  // formik already holds full option objects for the selection; feed them to
  // the select so tags show labels even while `options` is still loading
  const mergedOptions = [
    ...(options ?? []),
    ...selected.filter(
      (s) => !(options ?? []).some((option) => option.value === s.value),
    ),
  ];

  return (
    <FormField>
      <Stack alignX="space-between" alignY="center">
        {label && (
          <FormLabel>
            {label}
            {required && <span className="ml-1 text-red-500">*</span>}
          </FormLabel>
        )}
        {rightLabel}
      </Stack>
      <KoobInputSelect
        multiple
        searchable
        disabled={disabled}
        loading={loading}
        placeholder={placeholder}
        options={mergedOptions}
        value={selected.map((option) => option.value)}
        onValueChange={(values) => {
          // formik keeps whole option objects, as it always did
          const next = values
            .map(
              (v) =>
                (options ?? []).find((option) => option.value === v) ??
                selected.find((option) => option.value === v),
            )
            .filter(Boolean);
          const nextSelection = single ? next.slice(-1) : next;
          setValue(nextSelection);
          if (onSelectOption) {
            const added = next.find(
              (option) => !selected.some((s) => s.value === option.value),
            );
            if (added) {
              onSelectOption(added);
            }
          }
        }}
        renderOption={renderOption && ((option) => renderOption(option))}
        error={hasError}
        data-testid={name}
      />
      {Boolean(hasError) && (
        <ErrorMessageWrapper>{formatError(error)}</ErrorMessageWrapper>
      )}
    </FormField>
  );
};

export default LocalMultiSearchableSelectField;
